/**
 * "Recent changes" helpers (mockup 01 structured list, blueprint §7 C26/C27).
 * Pure. Each activity event becomes a row: a time, the event title, a helper
 * sub-line and a trailing tag naming the kind of change. A new drift takes the
 * high-contrast neutral tag (the Neutral Bad State Rule); everything else is
 * the default tag. No hue marks a state here.
 */
import { differenceInCalendarDays, format } from 'date-fns'
import type { ActivityItem } from '@/types'

export type ChangeTagKind = 'default' | 'high-contrast'

const KIND: Partial<Record<ActivityItem['type'], { label: string; kind?: ChangeTagKind }>> = {
  drift_detected: { label: 'New drift', kind: 'high-contrast' },
  drift_resolved: { label: 'Back to baseline' },
  drift_approved_by_user: { label: 'Accepted' },
  drift_rejected_by_user: { label: 'Rejected' },
  drift_remediated: { label: 'Remediated' },
  tenant_added: { label: 'Tenant added' },
  compliance_scan: { label: 'Assessed' },
  bpa_scan: { label: 'Assessed' },
  scuba_scan: { label: 'Assessed' },
  app_posture_scan: { label: 'Assessed' },
  baseline_refresh: { label: 'Baseline refreshed' },
}

/** The trailing tag for an event type. Failed and partial runs say so in words. */
export function changeTag(type: ActivityItem['type']): { label: string; kind: ChangeTagKind } {
  if (type.endsWith('_failed')) return { label: 'Failed', kind: 'default' }
  if (type.endsWith('_partial')) return { label: 'Partial', kind: 'default' }
  const k = KIND[type]
  return { label: k?.label ?? 'Updated', kind: k?.kind ?? 'default' }
}

function parse(ts: string): Date | null {
  const d = new Date(ts)
  return Number.isNaN(d.getTime()) ? null : d
}

/** The time column: "Today 06:12", "Tue 14:20" within the week, else "Sep 25". */
export function changeTime(ts: string, now: Date = new Date()): string {
  const d = parse(ts)
  if (!d) return ''
  const days = differenceInCalendarDays(now, d)
  if (days <= 0) return `Today ${format(d, 'HH:mm')}`
  if (days < 7) return format(d, 'EEE HH:mm')
  return format(d, 'MMM d')
}

/** The helper sub-line: who acted (when recorded) and the event's description. */
export function changeSub(e: Pick<ActivityItem, 'actionBy' | 'description'>): string | undefined {
  const parts = [e.actionBy?.trim() ? `By ${e.actionBy.trim()}` : null, e.description?.trim() || null].filter(Boolean)
  return parts.length ? parts.join(' · ') : undefined
}

/** "Sep 25 to Oct 2" across the shown events (oldest to newest), "Oct 2" for one day, null for none. */
export function changesRange(events: ReadonlyArray<Pick<ActivityItem, 'timestamp'>>): string | null {
  const dates = events
    .map((e) => parse(e.timestamp))
    .filter((d): d is Date => d != null)
    .sort((a, b) => a.getTime() - b.getTime())
  if (!dates.length) return null
  const first = format(dates[0], 'MMM d')
  const last = format(dates[dates.length - 1], 'MMM d')
  return first === last ? first : `${first} to ${last}`
}
